import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import type { Id, QltSlotAttendance, QltViolation } from '../../api/types';
import { Modal } from '../../components';
import { REASON_PRESETS } from '../../config/violation-reasons';
import { modalMeta } from '../periodLabels';
import { toggleItem } from './checklistText';
import { CONDUCT_RULES, draftRecord, emptyDraft, switchKind, type ConductDraft } from './conductEntry';
import { useMsg, type MaybeMsg } from '../../i18n/msg';
import '../../styles/class-management.css';

// the box holds the generated sentence as well as the teacher's words
const TEXT_LIMIT = 400;
const TYPE_KEY: Record<QltViolation['type'], 'violation.type.commendation' | 'violation.type.violation'> = { commendation: 'violation.type.commendation', violation: 'violation.type.violation' };

export interface ViolationFormValue {
  student: Id;
  type: QltViolation['type'];
  description: string;
}

export interface ViolationFormProps {
  open: boolean;
  classTitle: string;
  periodLabel?: string;
  subjectTitle?: string;
  initialType: QltViolation['type'];
  students: QltSlotAttendance[];
  saving: boolean;
  error: MaybeMsg | null;
  onClose: () => void;
  onSubmit: (value: ViolationFormValue) => void;
}

function TypeOption({ tone, active, onSelect }: { tone: QltViolation['type']; active: boolean; onSelect: () => void }) {
  const { t } = useTranslation(['panels', 'common']);
  return (
    <button type="button" role="radio" aria-checked={active} className={`cm-type cm-type--${tone}${active ? ' is-on' : ''}`} onClick={onSelect}>
      {t(TYPE_KEY[tone])}
    </button>
  );
}

export function ViolationForm({ open, classTitle, periodLabel, subjectTitle, initialType, students, saving, error, onClose, onSubmit }: ViolationFormProps) {
  const { t } = useTranslation(['panels', 'common']);
  const tr = useMsg();
  const [studentId, setStudentId] = useState('');
  const [draft, setDraft] = useState<ConductDraft>(() => emptyDraft(initialType));

  useEffect(() => {
    if (!open) return;
    setStudentId(students.length > 0 ? String(students[0].student._id || students[0].student.id || '') : '');
    setDraft(emptyDraft(initialType));
  }, [open, initialType, students]);

  const { type, entry } = draft;
  const record = draftRecord(draft);

  function toggleReason(reason: string) {
    setDraft((current) => ({ ...current, entry: toggleItem(current.entry, CONDUCT_RULES[current.type], reason) }));
  }

  function editText(text: string) {
    setDraft((current) => ({ ...current, entry: { ...current.entry, text } }));
  }

  // one record per save, of the active type; a failed save keeps the draft for a retry
  function submit() {
    if (record) onSubmit({ student: studentId, type: record.type, description: record.description });
  }

  const contentLabel = type === 'commendation' ? t('violation.content.commendation') : t('violation.content.violation');
  const actions = <button type="button" className="cm-btn-primary" disabled={saving || !studentId || !record} onClick={submit}>{saving ? t('common:states.saving') : t('logbook.confirm')}</button>;

  return (
    <Modal open={open} title={t('violation.title')} description={modalMeta({ classTitle, periodLabel, subjectTitle })} className="cm-modal cm-modal--fill" layerClassName="cm-layer--fill" actions={actions} onClose={onClose}>
      <div className="cm-type-toggle" role="radiogroup" aria-label={t('violation.typeLabel')}>
        <TypeOption tone="commendation" active={type === 'commendation'} onSelect={() => setDraft((current) => switchKind(current, 'commendation'))} />
        <TypeOption tone="violation" active={type === 'violation'} onSelect={() => setDraft((current) => switchKind(current, 'violation'))} />
      </div>
      <label className="cm-label" htmlFor="violation-student">{t('student.label')}</label>
      <select id="violation-student" className="cm-select" value={studentId} onChange={(event) => setStudentId(event.target.value)}>
        {students.length === 0 ? <option value="">{t('violation.noStudents')}</option> : students.map((row) => <option key={row.student._id} value={String(row.student._id || row.student.id)}>{row.student.name || row.student.code || t('student.fallback')}</option>)}
      </select>
      <div className="cm-label">{contentLabel}</div>
      <div className="cm-checklist" role="group" aria-label={contentLabel}>
        {REASON_PRESETS[type].map((reason) => (
          <label className={entry.ticks.indexOf(reason) >= 0 ? 'cm-check is-on' : 'cm-check'} key={reason}>
            <input type="checkbox" checked={entry.ticks.indexOf(reason) >= 0} onChange={() => toggleReason(reason)} />
            <span>{reason}</span>
          </label>
        ))}
      </div>
      <label className="cm-label" htmlFor="violation-text">{t('entry.text')}</label>
      <div className="cm-note">
        <textarea id="violation-text" value={entry.text} maxLength={TEXT_LIMIT} onChange={(event) => editText(event.target.value)} placeholder={t('violation.textPlaceholder')} />
        <span className="cm-note__count">{t('entry.count', { length: entry.text.length, limit: TEXT_LIMIT })}</span>
      </div>
      {error ? <p className="cm-error" role="alert">{tr(error)}</p> : null}
    </Modal>
  );
}
